// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
import React, { FC, createRef, KeyboardEvent, useState, Fragment, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { FormInstance } from 'antd/es/form'
import { useHistory } from 'react-router-dom'
import {
    Space, Form, Input, Button, Typography, List, Skeleton, Modal, notification
} from 'antd'
import { CheckCircleOutlined, FormOutlined, DeleteOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import type { IAppState } from '@/store/reducers'
import { getWorkgroup } from '@/store/actions/tableEdit.action'
import type { IWorkgroup } from '@/store/models/tableEdit.model'
import { getErrorStr } from '@/store/sagas/table.saga'
import { requestFail } from '@/store/actions/common.action'
import { addWorkgroup, delWorkgroup, editWorkgroup } from '@/services/table.service'
import NoSpaceInput from '@/components/noSpaceInput'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import Nav from '@/components/nav'

const WorkGroupEdit: FC = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const { t, i18n } = useTranslation()
    
    const authInfo = useSelector((state: IAppState) => state.auth.authInfo)
    const tableEditData = useSelector((state: IAppState) => state.tableEditData.table1EditData)
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)
    const route = useSelector((state: IAppState) => state.router)
    const [formWorkGroup, setFormWorkgroup] = useState<IWorkgroup | null>(null)
    const [formDataId, setFormDataId] = useState<number | null>(null)
    const formRef = createRef<FormInstance>()
    const { Text } = Typography

    const layout = {
        labelCol: { span: 6 },
        wrapperCol: { span: 10 },
    }
    const tailLayout = {
        wrapperCol: { offset: 6, span: 10 },
    }

    const popMsg = (msg: string) => {
        const args = {
            message: msg,
            description: '',
            icon: <CheckCircleOutlined style={{ color: '#08993f' }} />,
            duration: 3,
        }
        notification.open(args)
    }

    const onFinish = async (values: IWorkgroup) => {
        try {
            if (values.id) {
                // 调用修改接口
                await editWorkgroup('' + values.id, values.groupName)
            } else {
                // 调用添加接口
                // dispatch(addWorkgroup(values.groupName))
                await addWorkgroup(values.groupName, values.ptnHeader)
            }
            popMsg(t('save') + (i18n.language !== 'zh' ? ' ' : '') + t('success'))
            onReset()
            dispatch(getWorkgroup())
        } catch (error: any) {
            sessionStorage.removeItem('requestMsg')
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }
    const onReset = () => {
        setFormDataId(null)
        setFormWorkgroup(null)
        formRef.current?.resetFields()
    }
    const onEditWorkgroup = (item: IWorkgroup) => {
        setFormWorkgroup(item)
        setFormDataId(item.id)
        formRef.current?.setFieldsValue({
            id: item.id,
            groupName: item.groupName,
            ptnHeader: item.ptnHeader
        })
        window.scrollTo(0, 0)
    }
    const onDelWorkgroup = (item: IWorkgroup) => {
        console.log(item)
        Modal.confirm({
            title: t('confirmDel'),
            okText: t('confirm'),
            cancelText: t('cancel'),
            centered: true,
            async onOk () {
                try {
                    await delWorkgroup(item.id)
                    popMsg(t('delete') + (i18n.language !== 'zh' ? ' ' : '') + t('success'))
                    dispatch(getWorkgroup())
                } catch (error: any) {
                    const str = getErrorStr(error)
                    dispatch(requestFail(str))
                }
            }
        })
    }

    const routes = [
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/workgroupEdit',
            breadcrumbName: t('manage workgroup'),
        },
    ]
    useEffect(() => {
        // 获取 workgroupList
        dispatch(getWorkgroup())
    }, [])
    useEffect(() => {
        console.log('tableEditData: ', tableEditData)
        formRef.current?.setFieldsValue(tableEditData)
    }, [history, route])
    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                route={route}
                dispatch={dispatch}
            />
            {/* 新增权限 和 编辑权限 才显示 */}
            <Form {...layout} ref={formRef} name="control-ref" onFinish={onFinish}
                className={authInfo && 
                    ((!formDataId && Reflect.has(authInfo, 'addWorkgroup')) ||
                    (formDataId && Reflect.has(authInfo, 'editWorkgroup')))
                    ? 'edit_table' : 'hide'}
                style={{ paddingTop: 0 }}
            >
                <Fragment key={tableEditData.key}>
                    <Form.Item></Form.Item>
                    <Form.Item name="id" hidden><Input /></Form.Item>
                    <Form.Item name="groupName" label={t('Work Group')}
                        rules={[{ required: true, message: t('Work Group Required') }]}
                    >
                        <NoSpaceInput placeholder={t('Work Group Placeholder')} />
                    </Form.Item>
                    <Form.Item name="ptnHeader" label={t('Work Group PTN')}
                        rules={[{ required: true, message: t('Work Group PTN Required') }]}
                    >
                        {
                            formWorkGroup ? formWorkGroup.ptnHeader : (
                                <NoSpaceInput placeholder={t('Work Group PTN Placeholder')} />
                            )
                        }
                    </Form.Item>
                </Fragment>
                <Form.Item {...tailLayout} style={{ textAlign: 'center' }}>
                    <Space size={20}>
                        <Button type="primary" htmlType="submit">
                            {t('submit')}
                        </Button>
                        <Button htmlType="button" onClick={onReset}>
                            {t('reset')}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
            <List className="workground_ls"
                itemLayout="horizontal"
                dataSource={workgroupList}
                renderItem={item => (
                    <List.Item
                        actions={!item.loading ? [
                            authInfo && Reflect.has(authInfo, 'editWorkgroup') && 
                            <Text
                                className='txt_blue hand'
                                onClick={() => onEditWorkgroup(item)}
                                title={t('edit')}
                            >
                                <FormOutlined style={{ fontSize: 16 }} />
                            </Text>,
                            authInfo && Reflect.has(authInfo, 'delWorkgroup') && 
                            <Text 
                                className='txt_red hand' 
                                onClick={() => onDelWorkgroup(item)} 
                                title={t('delete')}
                            >
                                <DeleteOutlined style={{ fontSize: 16 }} />
                            </Text>
                        ] : []}
                    >
                        <Skeleton avatar title={false} loading={item.loading} active>
                            <List.Item.Meta
                                title={item.groupName}
                            />
                        </Skeleton>
                    </List.Item>
                )}
            />
        </Space>
    )
}

export default WorkGroupEdit
